import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Buildings, Users, ClipboardText, PencilSimple, CheckCircle, PauseCircle, Copy, DownloadSimple } from "@phosphor-icons/react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import api from "../lib/api";
import { formatApiError, formatDate } from "../lib/utils-extra";

const STATUS_COLOR = {
  active: "bg-[#16A34A]/10 text-[#16A34A]",
  trial: "bg-[#0EA5E9]/10 text-[#0EA5E9]",
  sponsored: "bg-[#0284C7]/10 text-[#0284C7]",
  internal_demo: "bg-[#475569]/10 text-[#475569]",
  suspended: "bg-[#D97706]/10 text-[#D97706]",
  archived: "bg-[#DC2626]/10 text-[#DC2626]",
};

const empty = () => ({
  name: "", legal_business_name: "", industry: "", contact_person: "",
  contact_email: "", contact_phone: "", status: "active",
  access_level: "basic", start_date: new Date().toISOString().slice(0, 10),
  expiry_date: "", notes: "",
  branding: { product_name: "", company_display_name: "", logo_url: "",
    primary_color: "#0EA5E9", secondary_color: "#0284C7",
    login_tagline: "Measure what matters. Coach what counts.", pdf_footer_text: "" },
  owner_name: "", owner_email: "", owner_password: "", seed_sample_data: true,
});

export function SuperAdminDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/super-admin/dashboard").then(({ data }) => setData(data)); }, []);
  if (!data) return <div className="text-sm text-[#475569]">Loading…</div>;
  const card = (label, value, accent = "#0EA5E9") => (
    <Card className="surface-card border-[#E2E8F0]" data-testid={`sa-stat-${label.toLowerCase().replace(/ /g,"-")}`}>
      <CardContent className="p-5">
        <div className="small-label">{label}</div>
        <div className="font-display text-3xl font-semibold mt-2" style={{ color: accent }}>{value}</div>
      </CardContent>
    </Card>
  );
  return (
    <div className="space-y-6" data-testid="super-admin-dashboard">
      <div>
        <div className="small-label">Platform</div>
        <h1 className="font-display text-3xl font-semibold mt-1">Platform Dashboard</h1>
        <p className="text-[#475569] text-sm mt-1">Client &amp; business overview across all tenants.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {card("Total Tenants", data.total_tenants)}
        {card("Active", data.active || 0, "#16A34A")}
        {card("Trial", data.trial || 0, "#0EA5E9")}
        {card("Sponsored", data.sponsored || 0, "#0284C7")}
        {card("Suspended", data.suspended || 0, "#D97706")}
        {card("Internal Demo", data.internal_demo || 0, "#475569")}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Upcoming renewals */}
        <Card className="surface-card border-[#E2E8F0]">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display text-lg font-semibold">Upcoming renewals</div>
              <Badge className="bg-[#D97706]/10 text-[#D97706] border-0">{data.upcoming_renewals.length}</Badge>
            </div>
            {data.upcoming_renewals.length === 0 && <div className="text-sm text-[#475569]">No renewals in the next 60 days.</div>}
            {data.upcoming_renewals.map((r) => (
              <Link key={r.id} to={`/admin/tenants/${r.id}`} className="flex items-center justify-between p-3 rounded-xl hover:bg-[#F8FAFC] border-b border-[#E2E8F0] last:border-0" data-testid={`renewal-${r.id}`}>
                <div>
                  <div className="font-medium text-sm">{r.name}</div>
                  <div className="text-xs text-[#475569]">Expires {r.expiry_date}</div>
                </div>
                <Badge className={`border-0 ${r.days_left < 0 ? "bg-[#DC2626]/10 text-[#DC2626]" : r.days_left <= 14 ? "bg-[#D97706]/10 text-[#D97706]" : "bg-[#0EA5E9]/10 text-[#0EA5E9]"}`}>
                  {r.days_left < 0 ? `${Math.abs(r.days_left)}d overdue` : `${r.days_left}d left`}
                </Badge>
              </Link>
            ))}
          </CardContent>
        </Card>

        {/* Recent tenant activity */}
        <Card className="surface-card border-[#E2E8F0]">
          <CardContent className="p-5">
            <div className="font-display text-lg font-semibold mb-3">Recent tenant activity</div>
            {data.recent_activity.length === 0 && <div className="text-sm text-[#475569]">No recent activity.</div>}
            {data.recent_activity.slice(0, 8).map((a) => (
              <div key={a.id} className="flex items-start gap-3 py-2.5 border-b border-[#E2E8F0] last:border-0">
                <div className="w-2 h-2 rounded-full bg-[#0EA5E9] mt-2 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium capitalize">{a.action?.replace(/_/g, " ")}</div>
                  <div className="text-xs text-[#475569] truncate">{a.record_label || a.user_email || "—"}</div>
                </div>
                <div className="text-xs text-[#94A3B8] shrink-0">{formatDate(a.timestamp)}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card className="surface-card border-[#E2E8F0]">
        <CardContent className="p-5">
          <div className="font-display text-lg font-semibold mb-3">Recently created tenants</div>
          {data.recent_tenants.map((t) => (
            <Link key={t.id} to={`/admin/tenants/${t.id}`} className="flex items-center justify-between p-3 rounded-xl hover:bg-[#F8FAFC] border-b border-[#E2E8F0] last:border-0">
              <div>
                <div className="font-medium">{t.name}</div>
                <div className="text-xs text-[#475569]">{t.industry || "—"} · {formatDate(t.created_at)}</div>
              </div>
              <Badge className={`${STATUS_COLOR[t.status] || STATUS_COLOR.active} border-0 capitalize`}>{t.status?.replace("_"," ")}</Badge>
            </Link>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

export function SuperAdminTenants() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [createdInfo, setCreatedInfo] = useState(null);
  const navigate = useNavigate();

  const reload = async () => {
    const { data } = await api.get("/tenants");
    setList(data);
  };
  useEffect(() => { reload(); }, []);

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success("Copied to clipboard");
  };

  const downloadWelcome = async (tenantId, name) => {
    try {
      const res = await api.get(`/tenants/${tenantId}/welcome-package/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `Welcome_Package_${(name || "tenant").replace(/ /g, "_")}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const save = async () => {
    try {
      const payload = { ...form };
      if (!payload.expiry_date) delete payload.expiry_date;
      if (!payload.contact_email) delete payload.contact_email;
      if (!payload.owner_email) {
        delete payload.owner_email; delete payload.owner_password; delete payload.owner_name;
      } else if (!payload.owner_password) {
        delete payload.owner_password; // backend auto-generates a temp password
      }
      const { data } = await api.post("/tenants", payload);
      toast.success("Tenant created");
      setOpen(false); setForm(empty()); reload();
      if (data?.owner_temp_password) setCreatedInfo(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleStatus = async (t, newStatus) => {
    try {
      await api.put(`/tenants/${t.id}`, { status: newStatus });
      toast.success(`Tenant ${newStatus}`);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="super-admin-tenants-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="small-label">Platform</div>
          <h1 className="font-display text-3xl font-semibold mt-1">Tenants</h1>
          <p className="text-[#475569] text-sm mt-1">All client workspaces.</p>
        </div>
        <Button data-testid="new-tenant-button" onClick={() => setOpen(true)} className="bg-[#0EA5E9] hover:bg-[#0284C7]">
          <Plus size={18} className="mr-1" /> New Tenant
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((t) => (
          <Card key={t.id} className="surface-card border-[#E2E8F0]" data-testid={`tenant-card-${t.id}`}>
            <CardContent className="p-5">
              <div
                className="cursor-pointer"
                onClick={() => navigate(`/admin/tenants/${t.id}`)}
                data-testid={`open-tenant-${t.id}`}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2"><Buildings size={18} weight="duotone" color="#0EA5E9" />
                      <div className="font-display text-lg font-semibold truncate">{t.name}</div>
                    </div>
                    <div className="text-xs text-[#475569] mt-1">{t.industry || "—"}</div>
                  </div>
                  <Badge className={`${STATUS_COLOR[t.status] || STATUS_COLOR.active} border-0 capitalize`}>
                    {t.status?.replace("_"," ")}
                  </Badge>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center mt-3">
                  <div><Users size={14} className="mx-auto text-[#475569]" /><div className="text-xs mt-1 font-mono-data">{t.usage.users}</div><div className="text-[10px] text-[#475569]">users</div></div>
                  <div><Users size={14} className="mx-auto text-[#475569]" /><div className="text-xs mt-1 font-mono-data">{t.usage.employees}</div><div className="text-[10px] text-[#475569]">staff</div></div>
                  <div><ClipboardText size={14} className="mx-auto text-[#475569]" /><div className="text-xs mt-1 font-mono-data">{t.usage.evaluations}</div><div className="text-[10px] text-[#475569]">evals</div></div>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button variant="outline" size="sm" onClick={() => navigate(`/admin/tenants/${t.id}`)} data-testid={`manage-tenant-${t.id}`}>
                  Manage
                </Button>
                {t.status === "active" ? (
                  <Button variant="outline" size="sm" onClick={() => toggleStatus(t, "suspended")}
                          data-testid={`suspend-tenant-${t.id}`}>
                    <PauseCircle size={14} className="mr-1" /> Suspend
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => toggleStatus(t, "active")}
                          data-testid={`activate-tenant-${t.id}`}>
                    <CheckCircle size={14} className="mr-1" /> Activate
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New tenant</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Create a new client workspace and optionally its first owner account.</DialogDescription>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
            <div className="sm:col-span-2">
              <Label className="small-label">Tenant Name</Label>
              <Input className="mt-1.5" data-testid="tenant-name-input"
                     value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div><Label className="small-label">Legal Name</Label><Input className="mt-1.5" value={form.legal_business_name} onChange={(e) => setForm({...form, legal_business_name:e.target.value})} /></div>
            <div><Label className="small-label">Industry</Label><Input className="mt-1.5" value={form.industry} onChange={(e) => setForm({...form, industry:e.target.value})} /></div>
            <div><Label className="small-label">Contact Person</Label><Input className="mt-1.5" value={form.contact_person} onChange={(e) => setForm({...form, contact_person:e.target.value})} /></div>
            <div><Label className="small-label">Contact Email</Label><Input type="email" className="mt-1.5" value={form.contact_email} onChange={(e) => setForm({...form, contact_email:e.target.value})} /></div>
            <div>
              <Label className="small-label">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({...form, status:v})}>
                <SelectTrigger className="mt-1.5" data-testid="tenant-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="sponsored">Sponsored</SelectItem>
                  <SelectItem value="internal_demo">Internal Demo</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="small-label">Access Level</Label>
              <Select value={form.access_level} onValueChange={(v) => setForm({...form, access_level:v})}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="basic">Basic</SelectItem>
                  <SelectItem value="full_access">Full Access</SelectItem>
                  <SelectItem value="sponsored_full_access">Sponsored Full Access</SelectItem>
                  <SelectItem value="internal_demo">Internal Demo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2 mt-2 p-3 rounded-xl bg-[#F1F5F9]">
              <div className="small-label mb-2">First Tenant Owner (optional)</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <Input placeholder="Name" value={form.owner_name} onChange={(e) => setForm({...form, owner_name:e.target.value})} />
                <Input placeholder="Email" type="email" value={form.owner_email} onChange={(e) => setForm({...form, owner_email:e.target.value})}
                       data-testid="tenant-owner-email" />
                <Input placeholder="Password (blank = auto-generate)" type="text" value={form.owner_password} onChange={(e) => setForm({...form, owner_password:e.target.value})}
                       data-testid="tenant-owner-password" />
              </div>
              <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer">
                <input type="checkbox" checked={form.seed_sample_data}
                       onChange={(e) => setForm({...form, seed_sample_data: e.target.checked})}
                       data-testid="tenant-seed-sample-checkbox" />
                Seed sample data (positions + employees + KPI templates + a sample evaluation)
              </label>
            </div>
            <div className="sm:col-span-2">
              <Label className="small-label">Notes</Label>
              <Textarea rows={2} className="mt-1.5" value={form.notes} onChange={(e) => setForm({...form, notes:e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-tenant-button" className="bg-[#0EA5E9] hover:bg-[#0284C7]">Create Tenant</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Owner credentials after creation */}
      <Dialog open={!!createdInfo} onOpenChange={(o) => !o && setCreatedInfo(null)}>
        <DialogContent className="max-w-md" data-testid="tenant-created-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2">
            <CheckCircle size={20} weight="duotone" color="#16A34A" /> Tenant owner created
          </DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-[#475569]">
            Share these credentials securely with the client. They will be required to set a new
            password at first login.
          </DialogDescription>
          <div className="rounded-xl bg-[#F1F5F9] border border-[#E2E8F0] p-4 space-y-3">
            <div>
              <div className="small-label mb-1">Owner email</div>
              <div className="font-mono-data text-sm">{createdInfo?.owner_email}</div>
            </div>
            <div>
              <div className="small-label mb-1">Temporary password</div>
              <div className="flex items-center gap-2">
                <code className="flex-1 font-mono-data text-sm bg-white border border-[#E2E8F0] rounded-lg px-3 py-2" data-testid="created-owner-temp-password">
                  {createdInfo?.owner_temp_password}
                </code>
                <Button variant="outline" size="sm" onClick={() => copy(createdInfo?.owner_temp_password)} data-testid="copy-owner-temp-password">
                  <Copy size={14} />
                </Button>
              </div>
            </div>
            {createdInfo?.seeded && (
              <div className="text-xs text-[#16A34A] flex items-center gap-1">
                <CheckCircle size={12} weight="fill" /> Sample data seeded (positions, employees, KPI templates, evaluation)
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => downloadWelcome(createdInfo?.id, createdInfo?.name)} data-testid="download-welcome-package-created" className="mr-auto">
              <DownloadSimple size={14} className="mr-1" /> Welcome Package
            </Button>
            <Button onClick={() => setCreatedInfo(null)} className="bg-[#0EA5E9] hover:bg-[#0284C7]">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
